/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import client from '../../utils/contentfulClient';
import {
  Grid,
  Pagination,
  PaginationItem,
  Box,
  Typography,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import BlogCard from './BlogCard';
import FooterComponent from '../../components/HomePage/Footer/FooterComponent';
import FooterMobileComponent from '../../components/HomePage/Footer/FooterMobileComponent';
import { useMobileContext } from '../../contexts/MobileContext';
import LoadingDots from '../../components/LoadingDots/LoadingDots';
import SeoTagsManager from '../../utils/SeoTagsManager';
import BlogCategoryNav from './BlogCategoryNav';
import BlogSidebarMobile from './BlogSidebarMobile';
import BreadcrumbComponent from '../../components/Breadcrumb/BreadcrumbComponent';
import { toTitleCase } from '../../utils/casing';

const FilterIcon = 'https://assets.askhippo.co.za/Icons/common/filter.svg';

const NON_CATEGORY_SLUGS = ['all', 'SortByYear'];

// BlogCard has a hard maxWidth of 380px; three cards + two 16px gaps (spacing={2})
// is the widest row the desktop grid actually renders. Bounding the breadcrumb and
// category nav to this same width keeps them flush with the card row instead of
// spanning the full (wider) page container.
const CARD_ROW_MAX_WIDTH = 380 * 3 + 16 * 2;

const BlogList = () => {
  const { category: categoryParam, page: pageParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPosts, setTotalPosts] = useState(0);
  const isMobile = useMobileContext();
  const isDesktop = Boolean(isMobile.isDesktop);
  const isMobileOrTablet = !isDesktop;
  const [showMobileFilterPanel, setShowMobileFilterPanel] = useState(false);
  const postsPerPage = 12;

  const category = categoryParam || 'all';
  const parsedPage = parseInt(pageParam, 10);
  const currentPage =
    Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
  const year = new URLSearchParams(location.search).get('Year');

  const categoryFilter = category !== 'all' && year === null ? category : 'all';
  const yearFilter = year;

  // Category/page switches happen instantly enough (client-side nav + a
  // fast Contentful fetch) that the loading spinner can flash for only a
  // frame or two — enforcing a 500ms floor keeps it visible long enough to
  // read as a deliberate transition rather than a flicker.
  const MIN_LOADER_MS = 500;

  useEffect(() => {
    let cancelled = false;
    let pendingTimeout;

    const fetchPosts = async () => {
      const startedAt = Date.now();
      try {
        setLoading(true);

        let response;

        // build base query
        const query = {
          content_type: 'blogPost',
          order: '-fields.blogDate',
          limit: postsPerPage,
          skip: (currentPage - 1) * postsPerPage,
        };

        // add category filter if provided and not 'all'
        if (category && category !== 'all' && category !== 'SortByYear') {
          // If category is a plain field value:
          query['fields.category'] = category;

          // If category is a linked entry ID in Contentful, use:
          // query['fields.category.sys.id'] = category;
        }

        // add year filter if provided (truthy non-empty)
        if (year !== undefined && year !== null && String(year).trim() !== '') {
          const y = parseInt(year, 10);
          if (!Number.isNaN(y)) {
            const startOfYear = `${y}-01-01`; // matches "2025-01-01"
            const startOfNext = `${y + 1}-01-01`; // matches "2026-01-01"

            query['fields.blogDate[gte]'] = startOfYear;
            query['fields.blogDate[lt]'] = startOfNext;
          }
        }

        // finally call Contentful once with the assembled query
        response = await client.getEntries(query);

        if (cancelled) return;
        setPosts(response.items);
        setTotalPosts(response.total);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        const elapsed = Date.now() - startedAt;
        const remaining = MIN_LOADER_MS - elapsed;

        if (remaining > 0) {
          pendingTimeout = setTimeout(() => {
            if (!cancelled) setLoading(false);
          }, remaining);
        } else if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      cancelled = true;
      clearTimeout(pendingTimeout);
    };
  }, [category, currentPage, year]);

  const totalPages = Math.ceil(totalPosts / postsPerPage);

  const breadcrumbItems =
    categoryParam && !NON_CATEGORY_SLUGS.includes(categoryParam)
      ? [
          { label: 'Home', href: '/' },
          { label: 'Blog', href: '/blog' },
          { label: toTitleCase(categoryParam) },
        ]
      : [{ label: 'Home', href: '/' }, { label: 'Blog' }];

  const basePath = categoryParam ? `/blog/${categoryParam}` : '/blog';

  const handlePageChange = (event, value) => {
    const path = value > 1 ? `${basePath}/page/${value}/` : `${basePath}/`;
    navigate(`${path}${location.search}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Derived purely from URL params, so the correct title/canonical can be
  // emitted immediately — even while posts are still loading — instead of
  // leaving the previous page's (or the default index.html) title showing
  // until the fetch resolves.
  const BASE_URL = 'https://www.hippo.co.za';
  const canonicalPath =
    currentPage > 1 ? `${basePath}/page/${currentPage}/` : `${basePath}/`;
  const seoOverrides = {
    canonical: `${BASE_URL}${canonicalPath}`,
    ...(currentPage > 1
      ? {
          title: `${SeoTagsManager.getConfig(basePath).title} | Page ${currentPage}`,
        }
      : {}),
  };

  if (error) return <p>Error: {error}</p>;

  return (
    <>
      {SeoTagsManager.render(basePath, seoOverrides)}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          pt: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            maxWidth: 1600,
            width: '100%',
            margin: '0 auto',
            px: { xs: 2, sm: 3, md: 4 },
            pb: isMobileOrTablet ? '64px' : 0,
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: CARD_ROW_MAX_WIDTH },
              mx: { xs: 0, md: 'auto' },
              marginBottom: 4,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <BreadcrumbComponent items={breadcrumbItems} />
            </Box>
            {isDesktop && (
              <BlogCategoryNav
                activeCategory={categoryFilter}
                activeYear={yearFilter}
              />
            )}
            <Box sx={{ width: '100%' }}>
              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 10,
                  }}
                >
                  <LoadingDots variant="otp" />
                </Box>
              ) : (
                <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
                  {posts && posts.length > 0 ? (
                    posts.map((post) => (
                      <Grid
                        item
                        key={post.sys.id}
                        xs={12}
                        sm={6}
                        md={3}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <BlogCard
                          title={post.fields.heading}
                          image={post.fields.headingImage.fields.file.url}
                          date={post.fields.blogDate}
                          blurb={post.fields.blurb}
                          slug={post.fields.slug}
                          category={post.fields.category}
                          icon={post.fields.iconName}
                          sourceCategory={category}
                        />
                      </Grid>
                    ))
                  ) : (
                    // Centered "No posts" message while keeping filters visible
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          height: 240,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          p: 4,
                          mt: 2,
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          No posts to show
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.8,
                            textAlign: 'center',
                            maxWidth: 520,
                          }}
                        >
                          There are no posts that match the current filters. Try
                          clearing the category or year filter to see more
                          posts.
                        </Typography>

                        {/* optional quick actions — remove if you don't want links/buttons here */}
                        <Box sx={{ mt: 2 }}>
                          <a
                            href="/blog/all/"
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            >
                              View all posts
                            </Typography>
                          </a>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>

            {/* Pagination - order 3 on mobile (bottom), hidden on desktop (will be rendered separately) */}
            {!loading && posts && posts.length > 0 && totalPages > 1 && (
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  justifyContent: 'center',
                  mt: 2,
                  mb: 2,
                  order: 3,
                  width: '100%',
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  boundaryCount={0}
                  siblingCount={0}
                  renderItem={(item) => (
                    <PaginationItem
                      slots={{ previous: ArrowBack, next: ArrowForward }}
                      {...item}
                    />
                  )}
                  color="primary"
                  size="medium"
                  sx={{
                    '& .MuiPagination-ul': {
                      flexWrap: 'nowrap',
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Pagination for desktop - shown only on desktop, below the filters+posts row */}
          {!loading && posts && posts.length > 0 && totalPages > 1 && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center',
                mt: 4,
                mb: 4,
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                renderItem={(item) => (
                  <PaginationItem
                    slots={{ previous: ArrowBack, next: ArrowForward }}
                    {...item}
                  />
                )}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </Box>

        {/* Mobile Filter Slide-Up */}
        <AnimatePresence>
          {isMobileOrTablet && showMobileFilterPanel && (
            <>
              <motion.div
                onClick={() => setShowMobileFilterPanel(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  backdropFilter: 'blur(4px)',
                  zIndex: 1400,
                }}
              />
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{
                  position: 'fixed',
                  overflow: 'hidden',
                  top: '250px',
                  left: 0,
                  width: '100%',
                  height: 'calc(100% - 250px)',
                  backgroundColor: '#FFFFFF',
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                  boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
                  zIndex: 1500,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <BlogSidebarMobile
                  activeCategory={categoryFilter}
                  activeYear={yearFilter}
                  onClose={() => setShowMobileFilterPanel(false)}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Mobile Filter Button */}
        {isMobileOrTablet && (
          <Box
            onClick={() => setShowMobileFilterPanel((prev) => !prev)}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '48px',
              backgroundColor: '#C7CA00',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: '24px',
              zIndex: 9999,
              cursor: 'pointer',
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.03em',
                color: '#162F3B',
              }}
            >
              Filter
            </Box>
            <Box
              component="span"
              sx={{
                width: '24px',
                height: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                src={FilterIcon}
                alt="Filter"
                style={{ width: '24px', height: '24px', display: 'block' }}
              />
            </Box>
          </Box>
        )}

        {isDesktop ? <FooterComponent /> : <FooterMobileComponent />}
      </Box>
    </>
  );
};

export default BlogList;
